import { createContext, useContext, useRef, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAccount, type Account } from "@/hooks/useAccount";
import { usePreview } from "@/lib/preview";
import { useRestClient } from "@/lib/restClient";

type SettingsUpdate = (settings: Account["settings"]) => Account["settings"];
interface SettingsOperation {
  update: SettingsUpdate;
}

interface AccountSettingsValue {
  settings: Account["settings"] | undefined;
  available: boolean;
  isPending: boolean;
  isError: boolean;
  updateSettings: (update: SettingsUpdate) => void;
}

const AccountSettingsContext = createContext<AccountSettingsValue | null>(null);

export function AccountSettingsProvider({ children }: { children: ReactNode }) {
  const account = useAccount().data;
  const preview = usePreview();
  const apiFetch = useRestClient();
  const queryClient = useQueryClient();
  const confirmed = useRef<Account | undefined>(undefined);
  const pending = useRef<SettingsOperation[]>([]);
  const available = !!account && !preview;

  function publish() {
    const saved = confirmed.current;
    if (!saved) return;
    const settings = pending.current.reduce(
      (current, operation) => operation.update(current),
      saved.settings,
    );
    queryClient.setQueryData<Account>(["account"], (current) => ({
      ...current,
      ...saved,
      settings,
    }));
  }

  const mutation = useMutation({
    scope: { id: "account-settings" },
    retry: false,
    mutationFn: ({ update }: SettingsOperation) => {
      const saved = confirmed.current;
      if (!saved) throw new Error("Account settings are not available.");
      return apiFetch<Account>("/api/account", {
        method: "POST",
        body: JSON.stringify({ settings: update(saved.settings) }),
      });
    },
    onMutate: async (operation) => {
      if (!pending.current.length)
        confirmed.current = queryClient.getQueryData<Account>(["account"]);
      pending.current.push(operation);
      await queryClient.cancelQueries({ queryKey: ["account"] });
      publish();
    },
    onSuccess: (saved) => {
      confirmed.current = { ...confirmed.current, ...saved };
    },
    onSettled: (_saved, _error, operation) => {
      pending.current = pending.current.filter((entry) => entry !== operation);
      publish();
    },
  });

  return (
    <AccountSettingsContext.Provider
      value={{
        settings: account?.settings,
        available,
        isPending: mutation.isPending,
        isError: mutation.isError,
        updateSettings: (update) => {
          if (available) mutation.mutate({ update });
        },
      }}
    >
      {children}
    </AccountSettingsContext.Provider>
  );
}

export function useOptionalAccountSettings() {
  return useContext(AccountSettingsContext);
}

export function useAccountSettings() {
  const context = useOptionalAccountSettings();
  if (!context) throw new Error("useAccountSettings must be used inside AccountSettingsProvider");
  return context;
}
